import React, { useState, useEffect } from "react";
import { Tooltip, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getEmployeeSchedule } from "services/CheckInCheckOut/CheckInAndBreaks";

import { DAYS_ORDER } from "components/Screens/Timesheet/helpers";

import "./WeeklyScheduleTooltip.scss";

export default function WeeklyScheduleTooltip({ employeeId, schedule: providedSchedule, isAdmin }) {
  const [schedule, setSchedule] = useState(providedSchedule || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (providedSchedule) {
      setSchedule(providedSchedule);
    } else {
      fetchSchedule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, providedSchedule]);

  const fetchSchedule = async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const response = await getEmployeeSchedule(employeeId);

      if (response?.schedule) {
        setSchedule(response.schedule);
      }
    } catch (error) {
      console.error("Error fetching schedule in WeeklyScheduleTooltip:", error);
    }

    setLoading(false);
  };

  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":").map(Number);
    const period = hours >= 12 ? "pm" : "am";
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
  };

  const getDaySchedule = (dayOfWeek) => {
    if (!schedule || !Array.isArray(schedule)) return null;
    return schedule.find((day) => day.dayOfWeek === dayOfWeek);
  };

  const renderTooltipContent = () => {
    if (loading) {
      return <div className="weekly-schedule-tooltip__loading">Loading...</div>;
    }

    if (!schedule || schedule.length === 0) {
      return <div className="weekly-schedule-tooltip__no-data">No schedule available</div>;
    }

    const currentDayOfWeek = new Date().getDay();

    return (
      <div className="weekly-schedule-tooltip__content">
        {DAYS_ORDER.map(({ label, dayOfWeek }) => {
          const daySchedule = getDaySchedule(dayOfWeek);
          const isOff = !daySchedule || !daySchedule.startTime || !daySchedule.endTime;
          const isCurrentDay = dayOfWeek === currentDayOfWeek;

          return (
            <div key={dayOfWeek} className="weekly-schedule-tooltip__row">
              <span
                className={`weekly-schedule-tooltip__day ${isCurrentDay ? "weekly-schedule-tooltip__day--current" : ""}`}
              >
                {label}
              </span>
              <span
                className={`weekly-schedule-tooltip__time ${isCurrentDay ? "weekly-schedule-tooltip__time--current" : ""}`}
              >
                {isOff ? "Off" : `${formatTime(daySchedule.startTime)} - ${formatTime(daySchedule.endTime)}`}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Tooltip
      title={renderTooltipContent()}
      placement={isAdmin ? "right-start" : "bottom"}
      arrow
      classes={{
        tooltip: "weekly-schedule-tooltip",
        arrow: "weekly-schedule-tooltip__arrow",
      }}
    >
      <IconButton size="small" className="weekly-schedule-tooltip__icon-button">
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}
